import React, { useState, useEffect, useCallback } from 'react';
import { inventoryAPI, suppliersAPI, categoriesAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Package,
  Plus,
  Trash2,
  Loader2,
  AlertTriangle,
  Truck,
  Edit3,
  TrendingDown,
  Search,
  Download
} from 'lucide-react';
import Layout from '../../components/Layout';

const InventoryPage = () => {
  const [inventory, setInventory] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('inventory');
  const [searchQuery, setSearchQuery] = useState('');
  const [showLowStock, setShowLowStock] = useState(false);
  
  // Dialog states
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Form states
  const [newItem, setNewItem] = useState({
    name: '', sku: '', description: '', category_id: '', supplier_id: '',
    quantity: 0, min_quantity: 5, unit: 'pcs', location: '', cost_per_unit: 0
  });
  const [newSupplier, setNewSupplier] = useState({
    name: '', contact_name: '', email: '', phone: '', address: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [inventoryRes, suppliersRes, categoriesRes] = await Promise.all([
        inventoryAPI.getAll({ low_stock: showLowStock || undefined }),
        suppliersAPI.getAll(),
        categoriesAPI.getAll()
      ]);
      setInventory(inventoryRes.data);
      setSuppliers(suppliersRes.data);
      setCategories(categoriesRes.data);
    } catch (err) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [showLowStock]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Item handlers
  const handleCreateItem = async () => {
    if (!newItem.name.trim()) {
      toast.error('Item name is required');
      return;
    }
    try {
      const response = await inventoryAPI.create({
        ...newItem,
        category_id: newItem.category_id || null,
        supplier_id: newItem.supplier_id || null
      });
      setInventory([...inventory, response.data]);
      resetItemForm();
      setItemDialogOpen(false);
      toast.success('Item created');
    } catch (err) {
      toast.error('Failed to create item');
    }
  };

  const handleUpdateItem = async () => {
    if (!editingItem) return;
    try {
      const response = await inventoryAPI.update(editingItem.id, {
        ...newItem,
        category_id: newItem.category_id || null,
        supplier_id: newItem.supplier_id || null
      });
      setInventory(inventory.map(i => i.id === editingItem.id ? response.data : i));
      resetItemForm();
      setItemDialogOpen(false);
      toast.success('Item updated');
    } catch (err) {
      toast.error('Failed to update item');
    }
  };

  const handleDeleteItem = async (id) => {
    try {
      await inventoryAPI.delete(id);
      setInventory(inventory.filter(i => i.id !== id));
      toast.success('Item deleted');
    } catch (err) {
      toast.error('Failed to delete item');
    }
  };

  const resetItemForm = () => {
    setNewItem({
      name: '', sku: '', description: '', category_id: '', supplier_id: '',
      quantity: 0, min_quantity: 5, unit: 'pcs', location: '', cost_per_unit: 0
    });
    setEditingItem(null);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setNewItem({
      name: item.name,
      sku: item.sku,
      description: item.description,
      category_id: item.category_id || '',
      supplier_id: item.supplier_id || '',
      quantity: item.quantity,
      min_quantity: item.min_quantity,
      unit: item.unit,
      location: item.location,
      cost_per_unit: item.cost_per_unit
    });
    setItemDialogOpen(true);
  };

  // Supplier handlers
  const handleCreateSupplier = async () => {
    if (!newSupplier.name.trim()) {
      toast.error('Supplier name is required');
      return;
    }
    try {
      const response = await suppliersAPI.create(newSupplier);
      setSuppliers([...suppliers, response.data]);
      setNewSupplier({ name: '', contact_name: '', email: '', phone: '', address: '' });
      setSupplierDialogOpen(false);
      toast.success('Supplier created');
    } catch (err) {
      toast.error('Failed to create supplier');
    }
  };

  const handleDeleteSupplier = async (id) => {
    try {
      await suppliersAPI.delete(id);
      setSuppliers(suppliers.filter(s => s.id !== id));
      toast.success('Supplier deleted');
    } catch (err) {
      toast.error('Failed to delete supplier');
    }
  };

  const handleExportInventory = () => {
    try {
      // Prepare export data
      const exportData = inventory.map(item => ({
        Name: item.name,
        SKU: item.sku || '',
        Description: item.description || '',
        Category: categories.find(c => c.id === item.category_id)?.name || '',
        Supplier: suppliers.find(s => s.id === item.supplier_id)?.name || '',
        Quantity: item.quantity,
        'Min Quantity': item.min_quantity,
        Unit: item.unit,
        Location: item.location || '',
        'Cost Per Unit': item.cost_per_unit || 0,
        'Total Value': (item.quantity * (item.cost_per_unit || 0)).toFixed(2),
        'Needs Reorder': item.needs_reorder ? 'Yes' : 'No'
      }));

      // Convert to CSV
      if (exportData.length === 0) {
        toast.error('No inventory items to export');
        return;
      }

      const headers = Object.keys(exportData[0]);
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => 
          headers.map(header => {
            const value = row[header];
            // Escape quotes and wrap in quotes if contains comma
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          }).join(',')
        )
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `inventory_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Exported ${exportData.length} inventory items`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export inventory');
    }
  };

  const filteredInventory = inventory.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.sku.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const lowStockCount = inventory.filter(i => i.needs_reorder).length;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="inventory-page">
        {/* Header */}
        <div>
          <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight text-white flex items-center gap-3">
            <Package className="w-10 h-10 text-orange-500" />
            Inventory
          </h1>
          <p className="text-zinc-400 mt-1">Manage parts, supplies, and equipment</p>
        </div>

        {/* Low Stock Alert */}
        {lowStockCount > 0 && (
          <div className="card-industrial border-red-500/50 bg-red-500/5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <div>
                <p className="text-red-400 font-medium">{lowStockCount} items need reordering</p>
                <p className="text-zinc-500 text-sm">Stock is at or below minimum level</p>
              </div>
            </div>
            <Button 
              onClick={() => setShowLowStock(!showLowStock)}
              className={showLowStock ? 'btn-primary' : 'btn-secondary'}
            >
              <TrendingDown className="w-4 h-4 mr-2" />
              {showLowStock ? 'Show All' : 'Show Low Stock'}
            </Button>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="inventory" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <Package className="w-4 h-4 mr-2" />
              Inventory ({inventory.length})
            </TabsTrigger>
            <TabsTrigger value="suppliers" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <Truck className="w-4 h-4 mr-2" />
              Suppliers ({suppliers.length})
            </TabsTrigger>
          </TabsList>

          {/* Inventory Tab */}
          <TabsContent value="inventory" className="mt-6">
            <div className="flex flex-col md:flex-row justify-between gap-4 mb-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  type="text"
                  placeholder="Search items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-industrial pl-10"
                  data-testid="search-input"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={handleExportInventory}
                  className="btn-secondary"
                  data-testid="export-inventory-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
                <Dialog open={itemDialogOpen} onOpenChange={(open) => { setItemDialogOpen(open); if (!open) resetItemForm(); }}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" data-testid="add-item-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Item
                    </Button>
                  </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="text-white">{editingItem ? 'Edit Item' : 'Add Item'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-zinc-400">Name *</Label>
                        <Input
                          value={newItem.name}
                          onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                          className="input-industrial mt-1"
                          placeholder="e.g., Light Bulb 60W"
                          data-testid="item-name-input"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400">SKU</Label>
                        <Input
                          value={newItem.sku}
                          onChange={(e) => setNewItem({ ...newItem, sku: e.target.value })}
                          className="input-industrial mt-1"
                          placeholder="e.g., LB-60W-001"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-zinc-400">Description</Label>
                      <Input
                        value={newItem.description}
                        onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                        className="input-industrial mt-1"
                        placeholder="Brief description"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-zinc-400">Category</Label>
                        <Select value={newItem.category_id || "none"} onValueChange={(v) => setNewItem({ ...newItem, category_id: v === "none" ? "" : v })}>
                          <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white mt-1">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            <SelectItem value="none">No category</SelectItem>
                            {categories.map(c => (
                              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-zinc-400">Supplier</Label>
                        <Select value={newItem.supplier_id || "none"} onValueChange={(v) => setNewItem({ ...newItem, supplier_id: v === "none" ? "" : v })}>
                          <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white mt-1">
                            <SelectValue placeholder="Select supplier" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            <SelectItem value="none">No supplier</SelectItem>
                            {suppliers.map(s => (
                              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-zinc-400">Quantity</Label>
                        <Input
                          type="number"
                          value={newItem.quantity}
                          onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 0 })}
                          className="input-industrial mt-1"
                          data-testid="item-quantity-input"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400">Min Qty (Alert)</Label>
                        <Input
                          type="number"
                          value={newItem.min_quantity}
                          onChange={(e) => setNewItem({ ...newItem, min_quantity: parseInt(e.target.value) || 0 })}
                          className="input-industrial mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400">Unit</Label>
                        <Select value={newItem.unit} onValueChange={(v) => setNewItem({ ...newItem, unit: v })}>
                          <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            <SelectItem value="pcs">Pieces</SelectItem>
                            <SelectItem value="kg">Kilograms</SelectItem>
                            <SelectItem value="liters">Liters</SelectItem>
                            <SelectItem value="meters">Meters</SelectItem>
                            <SelectItem value="boxes">Boxes</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-zinc-400">Storage Location</Label>
                        <Input
                          value={newItem.location}
                          onChange={(e) => setNewItem({ ...newItem, location: e.target.value })}
                          className="input-industrial mt-1"
                          placeholder="e.g., Shelf A-3"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400">Cost per Unit</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={newItem.cost_per_unit}
                          onChange={(e) => setNewItem({ ...newItem, cost_per_unit: parseFloat(e.target.value) || 0 })}
                          className="input-industrial mt-1"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <Button 
                      onClick={editingItem ? handleUpdateItem : handleCreateItem} 
                      className="btn-primary w-full" 
                      data-testid="save-item-btn"
                    >
                      {editingItem ? 'Update Item' : 'Create Item'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              </div>
            </div>

            <div className="space-y-3">
              {filteredInventory.map((item) => (
                <div 
                  key={item.id}
                  className={`card-industrial flex flex-col md:flex-row md:items-center gap-4 ${item.needs_reorder ? 'border-red-500/50' : ''}`}
                  data-testid={`item-${item.id}`}
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${item.needs_reorder ? 'bg-red-500/20' : 'bg-yellow-500/20'}`}>
                      <Package className={`w-6 h-6 ${item.needs_reorder ? 'text-red-400' : 'text-yellow-400'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-white font-medium">{item.name}</p>
                        {item.needs_reorder && (
                          <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-sm flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Low Stock
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-zinc-500">
                        {item.sku && <span>SKU: {item.sku}</span>}
                        {item.category_name && <span>{item.category_name}</span>}
                        {item.location && <span>@ {item.location}</span>}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${item.needs_reorder ? 'text-red-400' : 'text-white'}`}>
                        {item.quantity}
                      </p>
                      <p className="text-zinc-500 text-xs">{item.unit} (min: {item.min_quantity})</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        onClick={() => openEditDialog(item)}
                        className="btn-secondary p-2"
                      >
                        <Edit3 className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button className="bg-red-500/20 hover:bg-red-500/30 text-red-400 p-2">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-white">Delete Item</AlertDialogTitle>
                            <AlertDialogDescription className="text-zinc-400">
                              Are you sure you want to delete {item.name}?
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDeleteItem(item.id)} className="bg-red-500 hover:bg-red-600 text-white">
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              ))}
              {filteredInventory.length === 0 && (
                <div className="empty-state py-12">
                  <Package className="w-12 h-12 text-zinc-700 mb-3" />
                  <p className="text-zinc-500">
                    {searchQuery ? 'No items match your search' : 'No inventory items yet'}
                  </p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Suppliers Tab */}
          <TabsContent value="suppliers" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Suppliers</h2>
              <Dialog open={supplierDialogOpen} onOpenChange={setSupplierDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-supplier-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Supplier
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                  <DialogHeader>
                    <DialogTitle className="text-white">Add Supplier</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-zinc-400">Company Name *</Label>
                      <Input
                        value={newSupplier.name}
                        onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })}
                        className="input-industrial mt-1"
                        placeholder="e.g., ABC Supplies Inc."
                        data-testid="supplier-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">Contact Person</Label>
                      <Input
                        value={newSupplier.contact_name}
                        onChange={(e) => setNewSupplier({ ...newSupplier, contact_name: e.target.value })}
                        className="input-industrial mt-1"
                        placeholder="e.g., John Smith"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-zinc-400">Email</Label>
                        <Input
                          type="email"
                          value={newSupplier.email}
                          onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })}
                          className="input-industrial mt-1"
                          placeholder="email@example.com"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400">Phone</Label>
                        <Input
                          value={newSupplier.phone}
                          onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })}
                          className="input-industrial mt-1"
                          placeholder="+1 234 567 8900"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-zinc-400">Address</Label>
                      <Input
                        value={newSupplier.address}
                        onChange={(e) => setNewSupplier({ ...newSupplier, address: e.target.value })}
                        className="input-industrial mt-1"
                        placeholder="Full address"
                      />
                    </div>
                    <Button onClick={handleCreateSupplier} className="btn-primary w-full" data-testid="save-supplier-btn">
                      Create Supplier
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-3">
              {suppliers.map((supplier) => (
                <div 
                  key={supplier.id}
                  className="card-industrial flex items-center justify-between"
                  data-testid={`supplier-${supplier.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-sm flex items-center justify-center">
                      <Truck className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{supplier.name}</p>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-zinc-500">
                        {supplier.contact_name && <span>{supplier.contact_name}</span>}
                        {supplier.email && <span>{supplier.email}</span>}
                        {supplier.phone && <span>{supplier.phone}</span>}
                      </div>
                    </div>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button className="bg-red-500/20 hover:bg-red-500/30 text-red-400 p-2">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">Delete Supplier</AlertDialogTitle>
                        <AlertDialogDescription className="text-zinc-400">
                          Are you sure you want to delete {supplier.name}?
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDeleteSupplier(supplier.id)} className="bg-red-500 hover:bg-red-600 text-white">
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
              {suppliers.length === 0 && (
                <div className="empty-state py-12">
                  <Truck className="w-12 h-12 text-zinc-700 mb-3" />
                  <p className="text-zinc-500">No suppliers yet. Add your first supplier.</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default InventoryPage;
